import { useState, useEffect } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../ui/button';
import { Upload, X, FileText, CheckCircle2, AlertTriangle, Loader2, Search, Camera } from 'lucide-react';
import { toast } from 'sonner';

const isAllowed = (f) => {
  const name = (f?.name || '').toLowerCase();
  const type = f?.type || '';
  return type === 'application/pdf' || /\.pdf$/i.test(name)
      || type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp)$/i.test(name);
};

/**
 * Lieferschein-Upload-Dialog (25.08.2026 – ersetzt Rechnungs-Upload im Lager).
 * Neu vs. Invoice-Dialog:
 *  – Kein Preis (Lieferschein hat keine).
 *  – Nur Aktion `add_to_existing` (matched) oder `skip` (verwerfen).
 *  – Bei matchScore >= 0.9 wird die Zeile automatisch bestätigt (Toggle
 *    trotzdem änderbar).
 *  – Nach Upload wird die PDF automatisch als Mail an order@ees-… versandt
 *    (Backend erledigt das transparent).
 */
export default function DeliveryNoteUploadDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [dn, setDn] = useState(null);      // Server-Response nach Upload
  const [state, setState] = useState('idle');   // idle | uploading | parsed | confirming | done
  const [dragActive, setDragActive] = useState(false);
  const [rowActions, setRowActions] = useState([]); // { action, matchedItemId, itemSearch }
  const [items, setItems] = useState([]);

  useEffect(() => {
    authFetch('/api/inventory').then(r => r.ok ? r.json() : { items: [] })
      .then(d => setItems(d.items || d || []));
  }, []);

  const upload = async (forceDup = false) => {
    if (!file) return;
    setState('uploading');
    const fd = new FormData();
    fd.append('file', file);
    const url = forceDup ? '/api/inventory/delivery-notes?force=true' : '/api/inventory/delivery-notes';
    const r = await authFetch(url, { method: 'POST', body: fd });
    if (r.status === 409) {
      const d = await r.json();
      const detail = d.detail || d;
      const ok = window.confirm(
        `${detail.message}\n\nTrotzdem erneut importieren (Duplikat erzwingen)?`
      );
      if (ok) return upload(true);
      setState('idle');
      return;
    }
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail?.message || d.detail || 'Upload fehlgeschlagen');
      setState('idle');
      return;
    }
    const doc = await r.json();
    setDn(doc);
    setRowActions((doc.lineItems || []).map(li => ({
      action: li.autoConfirm ? 'add_to_existing' : (li.matched ? 'add_to_existing' : 'skip'),
      matchedItemId: li.matchedItemId || null,
    })));
    setState('parsed');
    if (doc.mailStatus === 'sent') toast.success('Lieferschein archiviert & per Mail an order@… versandt');
  };

  const confirm = async () => {
    setState('confirming');
    const payload = {
      lines: rowActions.map((s, i) => ({
        quantity: dn.lineItems[i].quantity,
        action: s.action,
        matchedItemId: s.matchedItemId,
      })),
    };
    const r = await authFetch(`/api/inventory/delivery-notes/${dn.id}/confirm`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Bestätigen fehlgeschlagen');
      setState('parsed');
      return;
    }
    toast.success('Lieferschein gebucht');
    setState('done');
    onDone?.();
  };

  const setAction = (idx, action) => setRowActions(s => s.map((x, i) => i === idx ? { ...x, action } : x));
  const setItem = (idx, itemId) => setRowActions(s => s.map((x, i) => i === idx ? { ...x, matchedItemId: itemId, action: 'add_to_existing' } : x));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="delivery-note-upload-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#32373c]">Lieferschein importieren</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="btn-dn-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {state === 'idle' && (
            <>
              <div
                onDrop={(e) => { e.preventDefault(); setDragActive(false);
                  const files = Array.from(e.dataTransfer.files);
                  const ok = files.find(isAllowed);
                  if (ok) setFile(ok); else toast.error('Bitte PDF oder Foto (JPG/PNG/HEIC) ablegen'); }}
                onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                onClick={() => document.getElementById('dn-file-input').click()}
                className={`rounded border-2 border-dashed p-8 text-center text-sm cursor-pointer transition-colors ${
                  dragActive ? 'border-[#9ec043] bg-[#f8faf2]' : 'border-gray-200 bg-gray-50 hover:border-[#9ec043]'
                }`}
                data-testid="dn-dropzone">
                <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div className="text-[#32373c] font-medium">Lieferschein hier ablegen oder auswählen</div>
                <div className="text-xs text-gray-400 mt-1">PDF oder Foto (JPG/PNG/HEIC) — auch aus Thunderbird (max. 25 MB)</div>
                <input id="dn-file-input" type="file"
                       accept="application/pdf,image/*"
                       className="hidden"
                       onChange={(e) => { const f = e.target.files?.[0]; if (f && isAllowed(f)) setFile(f); }} />
              </div>

              {/* 27.08.2026: Kamera-Button für Lagertablet-Foto-Upload direkt.
                  `capture="environment"` öffnet auf Mobil/Tablet die
                  Rückkamera-App ohne Umweg über die Datei-Auswahl. */}
              <div className="flex items-center gap-2 justify-center">
                <div className="h-px bg-gray-100 flex-1" />
                <span className="text-[10px] uppercase tracking-wide text-gray-400">oder</span>
                <div className="h-px bg-gray-100 flex-1" />
              </div>
              <button
                type="button"
                onClick={() => document.getElementById('dn-camera-input').click()}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-[#9ec043] bg-[#f8faf2] text-[#32373c] px-4 py-3 text-sm font-medium hover:bg-[#f0f6db] transition-colors"
                data-testid="btn-dn-camera">
                <Camera className="w-5 h-5" /> Lieferschein mit Kamera fotografieren
              </button>
              <input id="dn-camera-input" type="file" accept="image/*" capture="environment"
                     className="hidden"
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
              {file && (
                <div className="flex items-center justify-between rounded border border-gray-200 bg-white p-3 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#9ec043] shrink-0" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-gray-400 shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                  </div>
                  <Button onClick={() => upload(false)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-8"
                          data-testid="btn-dn-upload">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Hochladen & Analysieren
                  </Button>
                </div>
              )}
            </>
          )}

          {state === 'uploading' && (
            <div className="text-center py-10 text-sm text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              PDF wird ausgewertet — KI-Match läuft …
            </div>
          )}

          {state === 'parsed' && dn && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs" data-testid="dn-meta">
                <div><strong>Lieferant:</strong> {dn.supplier || '—'}</div>
                <div><strong>Lieferschein-Nr.:</strong> {dn.deliveryNoteNumber || '—'}</div>
                <div><strong>Datum:</strong> {dn.deliveryDate || '—'}</div>
                <div><strong>Positionen:</strong> {dn.lineItems.length}</div>
                {dn.mailStatus === 'sent' && (
                  <div className="text-emerald-700 mt-1">✓ PDF automatisch an order@ees-energiespeicher.de versandt</div>
                )}
              </div>
              <div className="border border-gray-200 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Position (Lieferschein)</th>
                      <th className="text-right px-2 py-1.5">Menge</th>
                      <th className="text-left px-2 py-1.5">Match → Lagerartikel</th>
                      <th className="text-left px-2 py-1.5">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dn.lineItems.map((li, i) => {
                      const st = rowActions[i] || {};
                      const chosen = items.find(x => x.id === st.matchedItemId);
                      const confidenceColor = li.matchScore >= 0.9 ? 'text-emerald-700 bg-emerald-50'
                        : li.matchScore >= 0.7 ? 'text-amber-700 bg-amber-50'
                        : 'text-red-700 bg-red-50';
                      return (
                        <tr key={i} data-testid={`dn-row-${i}`}>
                          <td className="px-2 py-1.5 align-top">
                            {li.articleNumber && <span className="text-gray-400 font-mono mr-1">{li.articleNumber}</span>}
                            {li.description}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums align-top">{li.quantity}</td>
                          <td className="px-2 py-1.5 align-top">
                            {chosen ? (
                              <div>
                                <div className="font-medium">{chosen.hersteller} {chosen.modell}</div>
                                {li.matchScore != null && (
                                  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded mt-0.5 ${confidenceColor}`}>
                                    {Math.round(li.matchScore * 100)}% Konfidenz {li.autoConfirm && '(auto)'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <ItemPicker items={items} onPick={(id) => setItem(i, id)} testid={`dn-picker-${i}`} />
                            )}
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <select value={st.action}
                                    onChange={(e) => setAction(i, e.target.value)}
                                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                                    data-testid={`dn-action-${i}`}>
                              <option value="add_to_existing" disabled={!st.matchedItemId}>Buchen</option>
                              <option value="skip">Verwerfen (Verbrauchsmaterial)</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} data-testid="btn-dn-cancel">Abbrechen</Button>
                <Button onClick={confirm} className="bg-[#9ec043] hover:bg-[#8aad38] text-white"
                        data-testid="btn-dn-confirm">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Bestätigen & buchen
                </Button>
              </div>
            </>
          )}

          {state === 'confirming' && (
            <div className="text-center py-10 text-sm text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Buche Bestand …
            </div>
          )}

          {state === 'done' && (
            <div className="text-center py-10">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <div className="text-sm text-[#32373c] font-medium">Lieferschein erfolgreich gebucht.</div>
              <Button onClick={onClose} className="mt-3">Schließen</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemPicker({ items, onPick, testid }) {
  const [q, setQ] = useState('');
  const rx = q.trim().toLowerCase();
  const hits = rx
    ? items.filter(x => `${x.hersteller} ${x.modell}`.toLowerCase().includes(rx)).slice(0, 8)
    : [];
  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <Search className="w-3 h-3 absolute top-2 left-2 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Lagerartikel suchen …"
               className="w-52 h-7 pl-6 pr-2 border border-gray-200 rounded text-xs"
               data-testid={testid} />
        {hits.length > 0 && (
          <div className="absolute z-10 bg-white border border-gray-200 rounded shadow mt-0.5 w-72">
            {hits.map(h => (
              <button key={h.id} onClick={() => { onPick(h.id); setQ(''); }}
                      className="block w-full text-left px-2 py-1 text-xs hover:bg-gray-50">
                {h.hersteller} — {h.modell}
              </button>
            ))}
          </div>
        )}
      </div>
      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="Kein automatisches Match — bitte Lagerartikel wählen oder Zeile verwerfen." />
    </div>
  );
}
